import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import { StyledDownloadLink, StyledDownloadLinkContainer } from './SortableTable.styles';

import ColumnHead from './ColumnHead';

import Table from '../Table';

const defaultSort = (a, b) => (a === b ? 0 : a > b ? 1 : -1);

const SortableTable = (props) => {
  const { alternateBackground, borderColor, columns, data, formatHeader, onReorder, intl, sortKey: initialSortKey } = props;

  const [sortKey, setSortKey] = useState(initialSortKey || columns[0].key);
  const [sortOrderIsAscending, setSortOrderIsAscending] = useState(true);
  const [sortedData, setSortedData] = useState(data);

  const getFilename = () => `Voyager table data ${moment(Date.now()).format('YYYY-MM-DD hh.mm.ss')}.csv`;
  const [filename, setFilename] = useState(getFilename());

  useEffect(() => {
    const dataCopy = [...data];
    const sortColumn = columns.find((column) => column.key === sortKey);
    const compare = sortColumn.compare ? sortColumn.compare : defaultSort;
    const reSortedData = dataCopy.sort((a, b) => compare(a[sortKey], b[sortKey]) * (sortOrderIsAscending ? 1 : -1));
    setSortedData(reSortedData);
  }, [columns, data, sortKey, sortOrderIsAscending]);

  useEffect(() => {
    if (onReorder) {
      onReorder({ sortKey, sortOrderIsAscending });
    }
  }, [onReorder, sortedData, sortKey, sortOrderIsAscending]);

  const headers = columns.map(({ key, intlId: id }) => ({
    key,
    label: id ? intl.formatMessage({ id }) : key
  }));

  const handleOnClick = (newSortKey) => {
    setSortKey(newSortKey);
    setSortOrderIsAscending(newSortKey === sortKey ? !sortOrderIsAscending : true);
  };

  const preparedDownloadData = useMemo(
    () =>
      sortedData.map((row) => ({
        ...row,
        ...(row?.coachAndSeatProducts?.length > 0 && {
          coachAndSeatProducts: row.coachAndSeatProducts.map((item) => `${item.carriage} / ${item.seatNumber}`).toString()
        }),
        ...(row?.coachAndSeatPassengers?.length > 0 && {
          coachAndSeatPassengers: row.coachAndSeatPassengers.map((item) => `${item.firstName} ${item.lastName.toUpperCase()}`).toString()
        }),
        ...(row?.extraItems?.length > 0 && {
          extraItems: row.extraItems.map((item) => item.productName ?? '--').toString()
        }),
        ...(row?.itemCoach?.length > 0 && {
          itemCoach: row.itemCoach.map((item) => item.itemCoach ?? '--').toString()
        }),
        ...(row?.trainSeal?.length > 0 && {
          trainSeal: row.trainSeal.map((item) => item.trainSeal ?? '--').toString()
        }),
        ...(row?.itemSeal?.length > 0 && {
          itemSeal: row.itemSeal.map((item) => item.itemSeal ?? '--').toString()
        }),
        ...(row?.customer &&
          row.customer?.name && {
            customer: Object.keys(row.customer)
              .map((key) => row.customer[key] ?? '--')
              .toString()
          })
      })),
    [sortedData]
  );

  return (
    <>
      <StyledDownloadLinkContainer>
        <StyledDownloadLink
          data-testid="sortable-table-csv-download-link"
          data={preparedDownloadData}
          headers={headers}
          filename={filename}
          asyncOnClick={true}
          onClick={() => setFilename(getFilename())}
        >
          <FormattedMessage id="sortableTable.downloadData" />
        </StyledDownloadLink>
      </StyledDownloadLinkContainer>
      <Table
        data-testid="sortable-table-table"
        formatHeader={(column) => (
          <ColumnHead
            {...{
              column,
              sortKey,
              sortOrderIsAscending
            }}
          >
            {formatHeader(column)}
          </ColumnHead>
        )}
        borderColor={borderColor}
        alternateBackground={alternateBackground}
        onHeaderClick={handleOnClick}
        sortable
        rows={sortedData}
        columns={columns}
      />
    </>
  );
};

SortableTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      format: PropTypes.func,
      compare: PropTypes.func
    }).isRequired
  ),
  borderColor: PropTypes.string,
  alternateBackground: PropTypes.object,
  sortKey: PropTypes.string,
  onReorder: PropTypes.func,
  sortOrderIsAscending: PropTypes.bool,
  formatHeader: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  })
};

export default SortableTable;
